import { useRouter as useCompatRouter } from "next/compat/router";
import {
  useParams,
  usePathname,
  useRouter as useAppRouter,
  useSearchParams,
} from "next/navigation";
import type { NextRouter } from "next/router";
import type { ParsedUrlQuery } from "querystring";
import type { UrlObject } from "url";
import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useLatestRef } from "./useLatestRef";

type RouterEvent =
  | "routeChangeStart"
  | "beforeHistoryChange"
  | "routeChangeComplete"
  | "routeChangeError"
  | "hashChangeStart"
  | "hashChangeComplete";
type RouterEventHandler = (...args: any[]) => void;

const handlers = new Map<RouterEvent, Set<RouterEventHandler>>();
let beforePopStateHandler: Parameters<NextRouter["beforePopState"]>[0] | null =
  null;
type NavigationOptions = { scroll?: boolean; shallow?: boolean };
let pendingAppNavigation:
  | { destination: string; options: NavigationOptions }
  | undefined;
// Set when a landed navigation was completed from `beginAppNavigation` ahead
// of the provider's location effect, which then skips its own completion.
let completedAheadOfLocationEffect = false;
const DashboardRouterContext = createContext<NextRouter | null>(null);

function isCurrentLocation(url: string): boolean {
  const { pathname, search } = new URL(url, window.location.href);
  return `${pathname}${search}` === location.pathname + location.search;
}

function completeAppNavigation() {
  if (!pendingAppNavigation) return;
  const { destination, options } = pendingAppNavigation;
  pendingAppNavigation = undefined;
  dashboardRouterEvents.emit("routeChangeComplete", destination, options);
}

// The location change completes a start, so a same-location navigation
// never begins one.
function beginAppNavigation(url: string, options: NavigationOptions = {}) {
  if (isCurrentLocation(url)) return;
  const { pathname, search, hash } = new URL(url, window.location.href);
  const destination = `${pathname}${search}${hash}`;
  if (pendingAppNavigation?.destination === destination) return;
  // Redirect stubs (`/[org]`, `/[org]/runs`, OrgRedirect) replace from their
  // mount effect, which runs before the provider's location effect has
  // completed the navigation that landed on them. Complete it here so the
  // chain reads start, complete, start, complete in order.
  if (
    pendingAppNavigation &&
    isCurrentLocation(pendingAppNavigation.destination)
  ) {
    completeAppNavigation();
    completedAheadOfLocationEffect = true;
  }
  pendingAppNavigation = { destination, options };
  dashboardRouterEvents.emit("routeChangeStart", destination, options);
}

// instrumentation-client hook: <Link> clicks and next/navigation pushes bypass
// the adapter, and the App Router only reaches this once it really navigates,
// so a prevented anchor click never announces a start.
export function onAppRouterTransitionStart(
  url: string,
  navigationType: "push" | "replace" | "traverse",
) {
  if (navigationType !== "traverse") beginAppNavigation(url);
}

export const dashboardRouterEvents = {
  on(event: RouterEvent, handler: RouterEventHandler) {
    const eventHandlers = handlers.get(event) ?? new Set();
    eventHandlers.add(handler);
    handlers.set(event, eventHandlers);
  },
  off(event: RouterEvent, handler: RouterEventHandler) {
    handlers.get(event)?.delete(handler);
  },
  emit(event: RouterEvent, ...args: any[]) {
    handlers.get(event)?.forEach((handler) => handler(...args));
  },
};

export const dashboardRoutePatterns = [
  "/admin/internal-monitors/[org]/[ruleId]",
  "/[org]/runs/[runId]/jobs/[jobId]",
  "/[org]/codesmith/[repo]/pr/[prNumber]",
  "/[org]/automations/[automationId]/runs",
  "/[org]/automations/[automationId]/edit",
  "/admin/internal-monitors/[org]",
  "/[org]/stickydisks/[repository]",
  "/[org]/sessions/[[...sessionPath]]",
  "/[org]/monitors/[monitorId]",
  "/[org]/runs/workflows",
  "/[org]/runs/jobs",
  "/[org]/runs/[runId]",
  "/[org]/canvas/[uuid]",
  "/[org]/cache/[repository]",
  "/[org]/automations/runs",
  "/admin/internal-monitors",
  "/admin/codesmith-leaderboard",
  "/[org]/settings/codesmith/environment",
  "/[org]/codesmith-autofix-configuration",
  "/[org]/settings/codesmith/skills",
  "/[org]/settings/codesmith/memory",
  "/[org]/settings/codesmith/mcp",
  "/[org]/settings/integrations",
  "/[org]/github-actions-analytics",
  "/[org]/settings/appearance",
  "/[org]/settings/codesmith",
  "/[org]/settings/features",
  "/[org]/settings/team",
  "/[org]/network-monitoring",
  "/[org]/mcp-connections",
  "/[org]/docker-analytics",
  "/[org]/automations",
  "/[org]/autofix-settings",
  "/[org]/stickydisks",
  "/[org]/testboxes",
  "/[org]/settings",
  "/[org]/support",
  "/[org]/monitors",
  "/[org]/rightsize",
  "/[org]/analytics",
  "/[org]/fusion-lab",
  "/[org]/usage",
  "/[org]/floor",
  "/[org]/cache",
  "/[org]/runs",
  "/[org]/logs",
  "/debug/sso-reauthorization",
  "/debug/session-inspector",
  "/codesmith-autofix-configuration",
  "/github-actions-analytics",
  "/network-monitoring",
  "/enable-autofix",
  "/docker-analytics",
  "/settings/[[...section]]",
  "/canvas-sandbox",
  "/autofix-settings",
  "/copy-prompt",
  "/stickydisks",
  "/rightsize",
  "/testboxes",
  "/extension",
  "/support",
  "/monitors",
  "/analytics",
  "/welcome",
  "/usage",
  "/cache",
  "/runs",
  "/logs",
  "/cli/auth",
  "/admin",
  "/",
  "/[org]",
  "/[...github]",
] as const;

type RouteParam = { name: string; catchAll: boolean };

function compileRoutePattern(pattern: string): {
  regex: RegExp;
  params: RouteParam[];
} {
  const params: RouteParam[] = [];
  if (pattern === "/") return { regex: /^\/$/, params };

  const segments = pattern.split("/").slice(1);
  const source = segments
    .map((segment) => {
      const optionalCatchAll = /^\[\[\.\.\.(.+)\]\]$/.exec(segment);
      if (optionalCatchAll) {
        params.push({ name: optionalCatchAll[1], catchAll: true });
        return "(?:/(.+?))?";
      }
      const catchAll = /^\[\.\.\.(.+)\]$/.exec(segment);
      if (catchAll) {
        params.push({ name: catchAll[1], catchAll: true });
        return "/(.+?)";
      }
      const dynamic = /^\[(.+)\]$/.exec(segment);
      if (dynamic) {
        params.push({ name: dynamic[1], catchAll: false });
        return "/([^/]+)";
      }
      return `/${segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`;
    })
    .join("");
  return { regex: new RegExp(`^${source}/?$`), params };
}

const dashboardRoutes = dashboardRoutePatterns.map((pattern) => ({
  pattern,
  ...compileRoutePattern(pattern),
}));

export function normalizeDashboardPathname(pathname: string): string {
  return (
    dashboardRoutes.find(({ regex }) => regex.test(pathname))?.pattern ??
    pathname
  );
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

// Dynamic params read off the pathname rather than `useParams`: Next syncs
// `usePathname` from `history.pushState` (see the shallow branch of
// `navigate`) but keeps the segment tree, so `useParams` would lag a shallow
// update the way `router.query` never did under the Pages Router.
export function dashboardRouteParams(
  pathname: string,
): Record<string, string | string[]> | null {
  for (const { regex, params } of dashboardRoutes) {
    const match = regex.exec(pathname);
    if (!match) continue;
    const values: Record<string, string | string[]> = {};
    params.forEach(({ name, catchAll }, index) => {
      const value = match[index + 1];
      if (value === undefined) return;
      values[name] = catchAll
        ? value.split("/").map(decodeSegment)
        : decodeSegment(value);
    });
    return values;
  }
  return null;
}

// Dynamic segments win over same-named search params, as under next/router.
export function dashboardQuery(
  params: Record<string, string | string[]> | null,
  searchParams: URLSearchParams,
): ParsedUrlQuery {
  const query: ParsedUrlQuery = {};
  for (const key of new Set(searchParams.keys())) {
    const values = searchParams.getAll(key);
    query[key] = values.length === 1 ? values[0] : values;
  }
  return { ...query, ...params };
}

function appendQueryValue(
  searchParams: URLSearchParams,
  key: string,
  value: unknown,
) {
  if (Array.isArray(value)) {
    value.forEach((entry) => appendQueryValue(searchParams, key, entry));
  } else if (value !== undefined && value !== null) {
    searchParams.append(key, String(value));
  }
}

export function serializeDashboardUrl(url: string | UrlObject): string {
  if (typeof url === "string") return url;

  const query =
    url.query && typeof url.query === "object" ? { ...url.query } : {};
  let pathname = url.pathname ?? "";
  pathname = pathname.replace(
    /\/\[\[\.\.\.([^\]]+)\]\]|\/\[\.\.\.([^\]]+)\]|\/\[([^\]]+)\]/g,
    (token, optionalCatchAll, catchAll, dynamic) => {
      const key = optionalCatchAll ?? catchAll ?? dynamic;
      const value = query[key];
      delete query[key];
      if (value === undefined || value === null || value === "") {
        return optionalCatchAll ? "" : token;
      }
      const parts = Array.isArray(value) ? value : [value];
      return `/${parts
        .map((part) => encodeURIComponent(String(part)))
        .join("/")}`;
    },
  );

  const searchParams = new URLSearchParams(url.search ?? "");
  Object.entries(query).forEach(([key, value]) =>
    appendQueryValue(searchParams, key, value),
  );
  const search = searchParams.toString();
  const hash = url.hash
    ? url.hash.startsWith("#")
      ? url.hash
      : `#${url.hash}`
    : "";
  return `${pathname}${search ? `?${search}` : ""}${hash}`;
}

export function DashboardRouterProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useAppDashboardRouter();
  return createElement(
    DashboardRouterContext.Provider,
    { value: router },
    children,
  );
}

type AppRouterNavigateOptions = { scroll?: boolean };

/**
 * next/navigation-shaped router over the adapter for callers that only need
 * `push`/`replace` with an href. A destination on the current pathname only
 * changes search params, which never renders a different segment on these
 * client-only pages, so it takes the history API instead of paying an RSC
 * round trip (filter, tab and date-range changes); it still scrolls to the
 * top unless `scroll: false`, as a real navigation would.
 *
 * Stable for the component's lifetime, like next/navigation's `useRouter`:
 * the adapter re-derives on every location change, including its own shallow
 * writes, so an effect keyed on this router would otherwise re-run per write.
 */
export function useDashboardAppRouter() {
  const routerRef = useLatestRef(useDashboardRouter());
  return useMemo(() => {
    const navigate = (
      method: "push" | "replace",
      href: string,
      options?: AppRouterNavigateOptions,
    ) => {
      const shallow =
        new URL(href, window.location.href).pathname ===
        window.location.pathname;
      void routerRef.current[method](href, undefined, {
        shallow,
        scroll: options?.scroll ?? true,
      });
    };
    return {
      push: (href: string, options?: AppRouterNavigateOptions) =>
        navigate("push", href, options),
      replace: (href: string, options?: AppRouterNavigateOptions) =>
        navigate("replace", href, options),
      prefetch: (href: string) => void routerRef.current.prefetch(href),
    };
  }, [routerRef]);
}

export function useDashboardRouter(): NextRouter {
  const providedRouter = useContext(DashboardRouterContext);
  const compatRouter = useCompatRouter();
  if (providedRouter) return providedRouter;
  if (!compatRouter) {
    throw new Error(
      "useDashboardRouter must be used within DashboardRouterProvider",
    );
  }
  return compatRouter;
}

function useAppDashboardRouter(): NextRouter {
  const compatRouter = useCompatRouter();
  const appRouter = useAppRouter();
  const appPathname = usePathname() ?? "";
  const appParams = useParams<Record<string, string | string[]>>();
  const nullableSearchParams = useSearchParams();
  const appSearchParams = useMemo(
    () => nullableSearchParams ?? new URLSearchParams(),
    [nullableSearchParams],
  );
  const search = appSearchParams.toString();
  const asPath = `${appPathname}${search ? `?${search}` : ""}`;
  const committedAsPath = useRef<string | null>(null);

  useEffect(() => {
    if (compatRouter) return;
    if (committedAsPath.current === asPath) return;
    const isInitialLocation = committedAsPath.current === null;
    committedAsPath.current = asPath;
    if (isInitialLocation) return;
    if (completedAheadOfLocationEffect) {
      completedAheadOfLocationEffect = false;
      return;
    }

    // Back/forward and anything else that moved the location without a
    // start announcement gets both events at once so pageviews still fire.
    if (!pendingAppNavigation) {
      dashboardRouterEvents.emit("routeChangeStart", asPath, {});
      dashboardRouterEvents.emit("routeChangeComplete", asPath, {});
      return;
    }
    completeAppNavigation();
  }, [asPath, compatRouter]);

  useEffect(() => {
    if (compatRouter) return;

    const handlePopState = (event: PopStateEvent) => {
      if (!beforePopStateHandler) return;
      const state = event.state ?? {};
      if (
        !beforePopStateHandler({
          url: window.location.pathname + window.location.search,
          as: window.location.pathname + window.location.search,
          options: state.options ?? {},
        })
      ) {
        window.history.forward();
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [compatRouter]);

  return useMemo(() => {
    if (compatRouter) return compatRouter;

    const pathname = normalizeDashboardPathname(appPathname);
    const navigate = async (
      method: "push" | "replace",
      url: string | UrlObject,
      _as?: string | UrlObject,
      options: NavigationOptions = {},
    ) => {
      const destination = serializeDashboardUrl(url);
      beginAppNavigation(destination, options);
      dashboardRouterEvents.emit("beforeHistoryChange", destination, options);

      // Like the Pages Router, `shallow` only holds within one route: the
      // native history API updates the URL (Next syncs `usePathname` and
      // `useSearchParams` from it) while the segment tree, and so every
      // component under it, stays mounted. A shallow push onto another route
      // is an ordinary navigation, and never scrolls unless asked to.
      const isShallow =
        options.shallow === true &&
        normalizeDashboardPathname(
          new URL(destination, window.location.href).pathname,
        ) === pathname;
      if (isShallow) {
        window.history[method === "push" ? "pushState" : "replaceState"](
          null,
          "",
          destination,
        );
        if (options.scroll) window.scrollTo(0, 0);
        return true;
      }

      appRouter[method](destination, { scroll: options.scroll });
      return true;
    };

    return {
      basePath: "",
      pathname,
      route: pathname,
      query: dashboardQuery(
        dashboardRouteParams(appPathname) ?? appParams,
        appSearchParams,
      ),
      asPath,
      back: appRouter.back,
      forward: appRouter.forward,
      reload: () => window.location.reload(),
      refresh: appRouter.refresh,
      push: (url, as, options) => navigate("push", url, as, options),
      replace: (url, as, options) => navigate("replace", url, as, options),
      prefetch: async (url) => appRouter.prefetch(serializeDashboardUrl(url)),
      beforePopState: (handler) => {
        beforePopStateHandler = handler;
      },
      events: dashboardRouterEvents,
      isFallback: false,
      isReady: true,
      isPreview: false,
      isLocaleDomain: false,
      locale: undefined,
      locales: undefined,
      defaultLocale: undefined,
      domainLocales: undefined,
    } as NextRouter;
  }, [
    appParams,
    appPathname,
    appRouter,
    appSearchParams,
    asPath,
    compatRouter,
  ]);
}
